import React, { useState, useRef, useEffect } from "react";
import { Modal } from "@mui/material";
import request from "../../Helpers/API";
import { formatearMensajePlantilla } from "./Helpers/MensajeVariables";
import * as signalR from "@microsoft/signalr";
import { useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/AddOutlined";
import AccountCircleIcon from '@mui/icons-material/AccountCircleOutlined';
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdfOutlined";
import DownloadIcon from "@mui/icons-material/DownloadOutlined";
import CallIcon from "@mui/icons-material/CallOutlined";
import CallEndIcon from "@mui/icons-material/CallEndOutlined";
import { useTwilioDevice } from "../../TwilioDeviceContext";

export const ChatScreen = ({ open, onClose, numeroTelefono = "", cliente, modulo, filtrado = false, onMensajeEnviado }) => {
  const [input, setInput] = useState("");
  const { idempleado, empleado } = useSelector((store) => store.usuario);

  const { roles } = useSelector((state) => state.sesion);


  const [dragActive, setDragActive] = useState(false);
  const [archivoPreview, setArchivoPreview] = useState(null);
  const [sendingFile, setSendingFile] = useState(false);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [estatus, setEstatus] = useState("D");
  const [loader, setLoader] = useState({
    mostrar: false,
    alerta: false,
    error: false,
    mensaje: "",
  });
  const urlEnviarMensaje = process.env.REACT_APP_URL_ENVIO_MENSAJE_TWILIO;
  const urlEnviarArchivo = process.env.REACT_APP_URL_ENVIO_ARCHIVO_TWILIO;
  const urlConnectionSignalR = process.env.REACT_APP_URL_CONNECTION_SIGNALR;

  const chatBodyRef = useRef(null);
  const connectionRef = useRef(null);

  // 📞 Llamadas de voz (Twilio Voice SDK) — Device compartido vía contexto global
  const {
    callStatus,
    numeroTelefonoActivo,
    nombreClienteActivo,
    iniciarLlamada,
    colgarLlamada,
    aceptarLlamada,
    rechazarLlamada,
  } = useTwilioDevice();
  const [duracionLlamada, setDuracionLlamada] = useState(0);

  //Suscripcion signalR
  useEffect(() => {
    let isMounted = true;

    // 🚫 no conectar si no hay teléfono válido
    if (!open || numeroTelefono === "Sin registrar" || numeroTelefono === "") {
      return;
    }

    // 🚨 evitar doble conexión
    if (connectionRef.current) return;
    // 
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(urlConnectionSignalR + "?apikey=SDDEW_rwerew_423545_323423")
      .withAutomaticReconnect()
      .build();

    connectionRef.current = connection;

    // 📩 recibir estatus
    connection.on("ReceiveStatus", (status) => {
      if (!isMounted) return;

      if (status === 1) {
        setEstatus("L");
        request
          .get(`Mensajeria/apartarChat?numeroTelefono=${numeroTelefono}`)
          .catch((error) => {
            setLoader((prev) => ({ ...prev, mostrar: false }));
            // console.error(error);
          });
      }
    });

    // 📩 recibir mensajes
    connection.on("ReceiveMessage", (mensaje) => {
      if (!isMounted) return;
      console.log("[SignalR] ReceiveMessage:", mensaje);

      setMessages((prev) => {
        const nextId =
          prev.length > 0
            ? Math.max(...prev.map((m) => m.idMensaje || 0)) + 1
            : 1;

        const { archivo, hora, extension, ...resto } = mensaje;
        const fechaRaw = hora ?? resto.fecha;
        return [...prev, {
          ...resto,
          idMensaje: nextId,
          documento: archivo ?? resto.documento,
          extension: extension ?? resto.extension,
          fecha: formatearFechaSignalR(fechaRaw),
        }];
      });
    });

    // 🚀 iniciar conexión
    connection
      .start()
      .then(async () => {
        setEstatus("O");

        await connection.invoke("JoinChat", numeroTelefono);
        await connection.invoke("UserEnteredChat", numeroTelefono);
      })
      .catch();

    // 🧹 cleanup SIEMPRE
    return () => {
      isMounted = false;

      const conn = connectionRef.current;
      connectionRef.current = null;

      if (!conn) return;

      const cleanup = async () => {
        try {
          if (conn.state === signalR.HubConnectionState.Connected) {
            await conn.invoke("LeaveChat", numeroTelefono);
          }
        } catch (err) {
          //   console.warn("LeaveChat falló (normal al cerrar):", err?.message);
        }

        try {
          await conn.stop();
        } catch (err) {
          //   console.warn("Stop falló:", err?.message);
        }
      };

      cleanup();

      request
        .get(`Mensajeria/eliminarApartado?numeroTelefono=${numeroTelefono}`)
        .catch((error) => {
          setLoader((prev) => ({ ...prev, mostrar: false }));
          //   console.error(error);
        });
    };
  }, [numeroTelefono, open]);

  // ⏱️ contador de duración de la llamada
  useEffect(() => {
    if (callStatus !== "en_llamada") {
      setDuracionLlamada(0);
      return;
    }

    const intervalo = setInterval(() => {
      setDuracionLlamada((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(intervalo);
  }, [callStatus]);

  const handleIniciarLlamada = () => iniciarLlamada(numeroTelefono);

  // 📞 colgar la llamada si el ChatScreen se cierra/desmonta mientras hay
  // una llamada activa (p. ej. se pierde la conexión y el modal se cierra
  // por fuera), para no dejarla "viva" sin interfaz visible.
  const callStatusRef = useRef(callStatus);
  callStatusRef.current = callStatus;

  useEffect(() => {
    if (!open && callStatusRef.current !== "idle") {
      colgarLlamada();
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (callStatusRef.current !== "idle") {
        colgarLlamada();
      }
    };
  }, []);

  // auto scroll
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!open) {
      setArchivoPreview(null);
    }
    if (numeroTelefono === "Sin registrar" || numeroTelefono === "") return;
    onExecute();
  }, [open]);

  //drag and drop para arrastrar archivos
  useEffect(() => {
    // if (!open) return;
    const handleDragEnter = (e) => {
      e.preventDefault();

      if (estatus !== "L" || sendingFile) return;

      // Solo activar si viene archivo (no texto)
      if (e.dataTransfer.types.includes("Files")) {
        setDragActive(true);
      }
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      if (estatus !== "L" || sendingFile) return;
      // Evita falsos negativos
      if (e.relatedTarget === null) {
        setDragActive(false);
      }
    };

    const handleDrop = (e) => {
      e.preventDefault();
      if (estatus !== "L" || sendingFile) return;
      setDragActive(false);

      const file = e.dataTransfer.files[0];
      if (file) handleFileUpload(file);
    };

    const handleDragOver = (e) => {
      if (estatus !== "L" || sendingFile) return;
      e.preventDefault();
    };


    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("drop", handleDrop);
    window.addEventListener("dragover", handleDragOver);

    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("drop", handleDrop);
      window.removeEventListener("dragover", handleDragOver);
    };
  }, [estatus]);

  const handleFileUpload = async (file) => {
    try {
      const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        alert("Solo se permiten JPG, PNG o PDF");
        return;
      }

      const toBase64 = (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
        });

      const base64File = await toBase64(file);

      setArchivoPreview({
        file,
        base64: base64File,
        nombre: file.name,
        tipo: file.type,
      });

    } catch (error) {
      console.error("Error procesando archivo", error);
    }
  };

  const handleClickAgregarArchivo = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // función que se ejecuta al seleccionar archivo desde explorador
  const handleChangeArchivoInput = (e) => {
    const file = e.target.files[0];
    if (file) handleFileUpload(file);
  };



  const onExecute = () => {
    setLoader({ ...loader, mostrar: true });

    request
      .get(`Mensajeria/ObtenerMensajes?numeroTelefono=${numeroTelefono}`)
      .then((result) => {
        setLoader({ ...loader, mostrar: false });
        if (Array.isArray(result) && result.length > 0) {
          setMessages(result);
        } else {
          setMessages([]);
        }
      })
      .catch((error) => {
        setLoader({ ...loader, mostrar: false });
        // console.error(error);
      });
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const nextId =
      messages.length > 0
        ? Math.max(...messages.map((m) => m.idMensaje || 0)) + 1
        : 1;

    const newMessage = {
      idMensaje: nextId,
      mensaje: input,
      createuser: idempleado,
      empleadoEnviado: empleado,
      estatus: "EN",
      mensajePlantilla: "",
      numeroTelefono: numeroTelefono,
      origino: "U",
      fecha: fechaAhora(),
    };

    // ✅ pintar inmediatamente (UX rápida)
    setMessages((prev) => [...prev, newMessage]);
    setInput("");

    // ✅ enviar al backend
    const url =
      `${urlEnviarMensaje}?` +
      `telefono=${encodeURIComponent(numeroTelefono)}` +
      `&mensaje=${encodeURIComponent(input)}` +
      `&usuario=${encodeURIComponent(idempleado)}`;
    // const url =
    //     `https://cb11-187-237-186-166.ngrok-free.app/api/v1/Twilio/Enviar_mensaje?` +
    //     `telefono=${encodeURIComponent(numeroTelefono)}` +
    //     `&mensaje=${encodeURIComponent(input)}` +
    //     `&usuario=${encodeURIComponent(idempleado)}`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": "MI_API_KEY_SUPER_SEGURA_123456",
        },
      });
      if (response.ok) {
        onMensajeEnviado?.();
      } else {
        // console.error("Error del servidor:", response.status);
      }
    } catch (error) {
      //   console.error("Error enviando mensaje:", error);
    }
  };

  const handleSendFile = async () => {
    if (!archivoPreview || sendingFile) return;

    setSendingFile(true);

    const nextId =
      messages.length > 0
        ? Math.max(...messages.map((m) => m.idMensaje || 0)) + 1
        : 1;

    const rawExtension = archivoPreview.tipo.split("/")[1];
    const extensionMap = { mpeg: "mp3", "x-m4a": "m4a", mp4: "m4a" };
    const extension = extensionMap[rawExtension] ?? rawExtension;
    const newMessage = {
      idMensaje: nextId,
      mensaje: `Archivo: ${archivoPreview.nombre}${input.trim() ? ` ${input.trim()}` : ""}`,
      documento: archivoPreview.base64,
      extension,
      createuser: idempleado,
      empleadoEnviado: empleado,
      estatus: "EN",
      mensajePlantilla: "",
      numeroTelefono: numeroTelefono,
      origino: "U",
      fecha: fechaAhora(),
    };

    setMessages((prev) => [...prev, newMessage]);

    try {
      await fetch(`${urlEnviarArchivo}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": "MI_API_KEY_SUPER_SEGURA_123456",
        },
        body: JSON.stringify({
          telefono: numeroTelefono,
          documento: archivoPreview.base64,
          extension: extension,
          nombre_archivo: archivoPreview.nombre,
          mensaje: input,
          usuario: idempleado,
        }),
      });

      setArchivoPreview(null);
      setInput("");
      setSendingFile(false);
      onMensajeEnviado?.();
    } catch (error) {
      console.error("Error enviando archivo", error);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSend();
  };

  const textoFinal = (plantilla, mensaje) => {
    return formatearMensajePlantilla(plantilla, mensaje);
  };

  const descargarArchivo = (base64, extension, nombre) => {
    const link = document.createElement("a");
    link.href = base64;
    link.download = nombre || `archivo.${extension}`;
    link.click();
  };

  const formatearFechaSignalR = (fechaStr) => {
    if (!fechaStr) return "";
    // Si ya está en formato dd/MM/yyyy lo devuelve tal cual
    if (/^\d{2}\/\d{2}\/\d{4}/.test(fechaStr)) return fechaStr;
    // Entrada: "yyyy-MM-dd HH:mm:ss"  →  Salida: "dd/MM/yyyy H:mm AM/PM"
    const [datePart, timePart = "00:00:00"] = fechaStr.split(" ");
    const [yyyy, mm, dd] = datePart.split("-");
    const [hhStr, minStr] = timePart.split(":");
    let h = parseInt(hhStr, 10);
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${dd}/${mm}/${yyyy} ${h}:${minStr} ${ampm}`;
  };

  const fechaAhora = () => {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, "0");
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const yyyy = now.getFullYear();
    let h = now.getHours();
    const min = String(now.getMinutes()).padStart(2, "0");
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${dd}/${mm}/${yyyy} ${h}:${min} ${ampm}`;
  };

  const formatearDuracion = (segundos) => {
    const m = String(Math.floor(segundos / 60)).padStart(2, "0");
    const s = String(segundos % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const extraerMensajeUsuario = (texto) => {
    if (!texto) return null;
    // Formato: "Archivo recibido: 20260617abc123jpg mensaje"
    //          "Archivo: 20260617abc123pdf mensaje"
    // El nombre no tiene espacios ni punto, la extensión está pegada al final
    const match = texto.match(
      /^archivo(?:\s+recibido)?:\s+\S+(?:jpeg|jpg|png|pdf|ogg|mp3|wav|m4a)\s*(.*)/i
    );
    if (!match) return texto;
    return match[1].trim() || null;
  };

  const AUDIO_EXTENSIONS = ["ogg", "mp3", "wav", "m4a"];

  const getMimeType = (extension) => {
    const map = { ogg: "audio/ogg", mp3: "audio/mpeg", wav: "audio/wav", m4a: "audio/mp4" };
    return map[extension?.toLowerCase()] ?? `audio/${extension}`;
  };


  const esMensajeLlamada = (msg) =>
    msg.mensajePlantilla?.trim().toLowerCase() === "llamada realizada";

  const renderContenidoMensaje = (msg) => {
    if (esMensajeLlamada(msg)) {
      return (
        <div className="message-call">
          <CallIcon className="message-call-icon" />
          <div className="message-call-info">
            <span className="message-call-title">Llamada realizada</span>
            {msg.mensaje && (
              <span className="message-call-subtitle">{msg.mensaje}</span>
            )}
          </div>
        </div>
      );
    }

    if (msg.documento) {
      const ext = msg.extension?.toLowerCase();
      const esImagen = ["jpg", "jpeg", "png"].includes(ext);
      const esPdf = ext === "pdf";
      const esAudio = AUDIO_EXTENSIONS.includes(ext);
      const nombreArchivo = `archivo.${msg.extension}`;
      const src = msg.documento.startsWith("data:")
        ? msg.documento
        : `data:${esImagen ? `image/${ext}` : esPdf ? "application/pdf" : getMimeType(ext)};base64,${msg.documento}`;

      if (esImagen) {
        const caption = extraerMensajeUsuario(msg.mensaje);
        return (
          <div className="message-media">
            <img
              src={src}
              alt="imagen"
              className="message-image"
              onClick={() => descargarArchivo(src, msg.extension, nombreArchivo)}
            />
            {caption && <span className="message-caption">{caption}</span>}
          </div>
        );
      }

      if (esPdf) {
        const caption = extraerMensajeUsuario(msg.mensaje);
        return (
          <div className="message-pdf-wrapper">
            <div
              className="message-pdf"
              onClick={() => descargarArchivo(src, msg.extension, nombreArchivo)}
            >
              <PictureAsPdfIcon className="pdf-icon" />
              <span className="pdf-label">{nombreArchivo}</span>
              <span className="pdf-download">⬇</span>
            </div>
            {caption && <span className="message-caption">{caption}</span>}
          </div>
        );
      }

      if (esAudio) {
        const caption = extraerMensajeUsuario(msg.mensaje);
        return (
          <div className="message-audio-wrapper">
            <div className="message-audio">
              <audio controls className="audio-player">
                <source src={src} type={getMimeType(ext)} />
              </audio>
            </div>
            {caption && <span className="message-caption">{caption}</span>}
          </div>
        );
      }
    }

    if (msg.origino === "S" && !esMensajeLlamada(msg)) {
      return textoFinal(msg.mensajePlantilla, msg.mensaje);
    }
    return msg.mensaje;
  };

  const ultimoResponsable = [...messages]
    .reverse()
    .find(msg => {
      const responsableValido = msg.responsable?.trim();
      if (!responsableValido) return false;
      if (modulo) {
        return msg.modulo === modulo;
      }
      return true;
    });

  const messagesFiltrados = messages.filter(msg => {
    if (!filtrado) return true;

    if (!msg.modulo) return false;

    if (msg.modulo === "TODO") return true;

    return msg.modulo === modulo;
  });

  return (
    <Modal open={open} onClose={callStatus === "idle" ? onClose : undefined}>
      <div className="modal-chat-main-container">
        <div className="chat-window">
          {/* HEADER */}
          <div className="chat-header">
            <div className="chat-user">
              <div className="avatar">
                <img src={`/static/media/imagenes/logomaquinaria.jpeg`} />{" "}
              </div>
              <div>
                <div className="name">{cliente || "Cliente"}</div>
                <div className="responsable">
                  <AccountCircleIcon /> {ultimoResponsable?.responsable || "Sin asignar"}
                </div>
                <div className="status">
                  <span className={`status-dot status-${estatus || "D"}`} />
                  {estatus === "L"
                    ? "En línea"
                    : estatus === "O"
                      ? "Ocupado"
                      : "Deshabilitado"}
                </div>
              </div>
            </div>

            <div className="header-actions">
              {/* {callStatus === "idle" &&  */}
              {(roles.some(rol => rol.idrol === 'REWR') && callStatus === "idle") &&
                (
                  <button
                    className="call-btn"
                    onClick={handleIniciarLlamada}
                    disabled={estatus === "O"}
                    title={estatus === "O" ? "Cliente ocupado" : "Llamar"}
                  >
                    <CallIcon />
                  </button>
                )
              }

              {callStatus === "idle" && (
                <button className="close-btn" onClick={onClose}>
                  ✕
                </button>
              )}
            </div>
          </div>

          {callStatus === "sonando" ? (
            /* ===== INTERFAZ DE LLAMADA ENTRANTE ===== */
            <div className="call-screen call-screen-incoming">
              <div className="call-avatar">
                <img src={`/static/media/imagenes/logomaquinaria.jpeg`} alt="avatar" />
              </div>
              <div className="call-name">{nombreClienteActivo || cliente || "Cliente"}</div>
              {(numeroTelefonoActivo || numeroTelefono) && (
                <div className="call-numero">{numeroTelefonoActivo || numeroTelefono}</div>
              )}
              <div className="call-status-label">Llamada entrante...</div>

              <div className="call-actions call-actions-incoming">
                <button className="call-reject-btn" onClick={rechazarLlamada} title="Rechazar">
                  <CallEndIcon />
                </button>
                <button className="call-accept-btn" onClick={aceptarLlamada} title="Aceptar">
                  <CallIcon />
                </button>
              </div>
            </div>
          ) : callStatus === "conectando" || callStatus === "en_llamada" ? (
            /* ===== INTERFAZ DE LLAMADA ACTIVA ===== */
            <div className="call-screen">
              <div className="call-avatar">
                <img src={`/static/media/imagenes/logomaquinaria.jpeg`} alt="avatar" />
              </div>
              <div className="call-name">{nombreClienteActivo || cliente || "Cliente"}</div>
              {(numeroTelefonoActivo || numeroTelefono) && (
                <div className="call-numero">{numeroTelefonoActivo || numeroTelefono}</div>
              )}
              <div className="call-status-label">
                {callStatus === "conectando" ? "Llamando..." : formatearDuracion(duracionLlamada)}
              </div>

              <div className="call-actions">
                <button className="call-hangup-btn" onClick={colgarLlamada} title="Colgar">
                  <CallEndIcon />
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* BODY */}
              <div className="chat-body" ref={chatBodyRef}>
                {messagesFiltrados
                  .slice() // 🔥 evita mutar el estado original
                  .sort((a, b) => a.idMensaje - b.idMensaje)
                  .map((msg, index) => (
                    <div
                      key={`${msg.idMensaje}-${index}`}
                      className={`message ${msg.origino === "C" ? "message-left" : "message-right"
                        } ${esMensajeLlamada(msg) ? "message-is-call" : ""}`}
                    >
                      {esMensajeLlamada(msg)
                        ? renderContenidoMensaje(msg)
                        : msg.documento
                          ? renderContenidoMensaje(msg)
                          : msg.origino === "S"
                            ? textoFinal(msg.mensajePlantilla, msg.mensaje)
                            : msg.mensaje}

                      {msg.origino === "C" && (
                        <span className="time">
                          {msg.fecha}
                        </span>
                      )}

                      {msg.origino !== "C" && (
                        <span className="time">{msg.empleadoEnviado} <br /> {msg.fecha}</span>
                      )}
                    </div>
                  ))}
              </div>

              {/* FOOTER */}
              {archivoPreview && (
                <div className="file-preview-container">
                  <div className="file-preview-card">
                    <div className="preview-close" onClick={() => setArchivoPreview(null)}>
                      ✕
                    </div>

                    <div className="file-info">
                      <strong>{archivoPreview.nombre}</strong>
                      <span>{archivoPreview.tipo}</span>
                    </div>

                    {archivoPreview.tipo.startsWith("image") && (
                      <img
                        src={archivoPreview.base64}
                        alt="preview"
                        className="preview-image"
                      />
                    )}

                    {archivoPreview.tipo === "application/pdf" && (
                      <div className="preview-pdf">
                        📄 PDF listo para enviar
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="chat-footer">
                <input
                  type="text"
                  ref={inputRef}
                  placeholder="Escribe un mensaje..."
                  value={input}
                  onChange={(e) => {
                    const value = e.target.value;

                    const formatted =
                      value.length > 0
                        ? value.charAt(0).toUpperCase() + value.slice(1)
                        : "";

                    setInput(formatted);
                  }}
                  onKeyDown={estatus === "L" && !archivoPreview ? handleKeyDown : undefined}
                  disabled={estatus !== "L"}
                  onPaste={async (e) => {
                    if (archivoPreview) return;
                    const items = e.clipboardData.items;
                    if (!items) return;

                    for (let i = 0; i < items.length; i++) {
                      const item = items[i];

                      if (item.kind === "file") {
                        const file = item.getAsFile();
                        if (!file) continue;

                        const base64File = await new Promise((resolve, reject) => {
                          const reader = new FileReader();
                          reader.readAsDataURL(file);
                          reader.onload = () => resolve(reader.result);
                          reader.onerror = (err) => reject(err);
                        });

                        setArchivoPreview({
                          file,
                          base64: base64File,
                          nombre: file.name || "clipboard.png",
                          tipo: file.type || "image/png",
                        });
                      }
                    }
                  }}
                />

                <button
                  className={`add-file-btn ${estatus === "L" && !sendingFile ? "" : "disabled"
                    }`}
                  onClick={handleClickAgregarArchivo}
                  disabled={archivoPreview || estatus !== "L" || sendingFile}
                >
                  <AddIcon />
                </button>

                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleChangeArchivoInput}
                  accept=".jpg,.jpeg,.png,.pdf"
                />
                <button
                  className={`send-btn ${estatus === "L" && !sendingFile ? "" : "disabled"
                    }`}
                  onClick={
                    estatus === "L" && !sendingFile
                      ? archivoPreview
                        ? handleSendFile
                        : handleSend
                      : undefined
                  }
                  disabled={estatus !== "L" || sendingFile}
                >
                  ➤
                </button>
              </div>
              {dragActive && (
                <div className="drag-overlay">
                  <div className="drag-content">
                    📎 Suelta el archivo para enviarlo
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};
